import React, { useEffect, useState, useRef } from "react";
import {
    Modal as AntdModal,
    Form,
    Input,
    Button,
    Tag,
    Space,
    Typography,
    Spin,
    Select,
    DatePicker,
} from "antd";
import api from "../../../../services/api";
import { NotebookPen } from "lucide-react";
import { notify } from "../../../../services/toast.config";

const { Text } = Typography;
const { Option } = Select;

interface CallData { mobileNumber: string; name: string; id: any; }
interface CompanyData { companyId: string; companyName: string; companyType: string; }
interface FormField {
    id: number;
    label: string;
    type: "text" | "select" | "address" | "textarea" | string;
    options?: string[];
    required: boolean;
}
interface LeadForm { key: string | number; LeadFormName: string; pipeline_id: string; structure: FormField[]; }
export interface AddLeadToZyvoProps {
    open: boolean;
    callData: CallData | null;
    selectedCompany: CompanyData;
    signature: string | null;
    user: any;
    onSuccess: () => void;
    onSkip: () => void;
}

const isMobileField = (label: string) => /mobile|phone|contact/i.test(label);
const isNameField = (label: string) => /^(full\s?name|name|student\s?name|lead\s?name)$/i.test(label.trim());

const MODAL_STYLES = {
    content: { padding: 0, display: "flex", flexDirection: "column" as const, minHeight: "90vh", maxHeight: "96vh", overflow: "hidden", borderRadius: 12 },
    header: { flexShrink: 0, padding: "16px 24px 12px", borderBottom: "1px solid #f0f0f0", marginBottom: 0 },
    body: { flex: 1, overflowY: "auto" as const, overflowX: "hidden" as const, padding: "20px 24px 8px", scrollbarWidth: "thin" as any, scrollbarColor: "#d1d5db transparent" },
    footer: { flexShrink: 0, padding: "12px 24px 16px", borderTop: "1px solid #f0f0f0", marginTop: 0 },
    mask: { position: "fixed" as const, inset: 0, width: "100vw", height: "100vh", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)", background: "rgba(0, 0, 0, 0.55)" },
};

const AddLeadToZyvo: React.FC<AddLeadToZyvoProps> = ({
    open, callData, selectedCompany, signature, user, onSuccess, onSkip,
}) => {
    const [forms, setForms] = useState<LeadForm[]>([]);
    const [selectedFormId, setSelectedFormId] = useState<string>("");
    const [selectedFormData, setSelectedFormData] = useState<LeadForm | null>(null);
    const [pipelines, setPipelines] = useState<string[]>([]);
    const [selectedPipeline, setSelectedPipeline] = useState<string>("");

    const [responses, setResponses] = useState<Record<string, string>>({});

    const [countries, setCountries] = useState<any[]>([]);
    const [statesAll, setStatesAll] = useState<any[]>([]);
    const [districtAll, setDistrictAll] = useState<any[]>([]);
    const [citiesAll, setCitiesAll] = useState<any[]>([]);
    const [states, setStates] = useState<any[]>([]);
    const [districts, setDistricts] = useState<any[]>([]);
    const [cities, setCities] = useState<any[]>([]);
    const [selectedCountry, setSelectedCountry] = useState<string>("");
    const [selectedState, setSelectedState] = useState<string>("");

    const [isLoadingForms, setIsLoadingForms] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [isDuplicate, setIsDuplicate] = useState(false);
    const [earlyDuplicate, setEarlyDuplicate] = useState(false);
    const [duplicateInfo, setDuplicateInfo] = useState<string | null>(null);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [isNumberSafe, setIsNumberSafe] = useState(false);

    const formRef = useRef<HTMLDivElement | null>(null);
    const pipelineRef = useRef<HTMLDivElement | null>(null);
    const fieldsRef = useRef<HTMLDivElement | null>(null);

    const checkDuplicateLead = async (mobileNumber: string, formId?: string) => {
        if (!mobileNumber || !signature) return;
        // Only check if we have a form selected — duplicate is scoped per form
        const resolvedFormId = formId ?? selectedFormId;
        if (!resolvedFormId) return;
        try {
            setIsCheckingDuplicate(true);
            setEarlyDuplicate(false);
            setDuplicateInfo(null);
            setIsNumberSafe(false);

            const res = await api.post("leadService/checkDuplcateLeadByNumber", {
                signature,
                mobile_number: `${mobileNumber}`,
                form_id: Number(resolvedFormId),
            });

            const data = res?.data?.message;
            if (data?.isDuplicate === true) {
                let message = data?.message || "This mobile number already exists in Zyvo.";
                setEarlyDuplicate(true);
                setDuplicateInfo(message);
                notify.warn(message);
            } else {
                setIsNumberSafe(true);
            }
        } catch (e: any) {
            const data = e?.data?.message;
            if (data?.isDuplicate === true) {
                let message = data.message || "This mobile number already exists in Zyvo.";
                setEarlyDuplicate(true);
                setDuplicateInfo(message);
                notify.warn(message);
            }
        } finally {
            setIsCheckingDuplicate(false);
        }
    };

    // Re-run duplicate check whenever the selected form changes (if mobile is available).
    useEffect(() => {
        if (!callData?.mobileNumber || !selectedFormId) return;
        // Reset any stale duplicate state first, then re-check against the new form
        setEarlyDuplicate(false);
        setDuplicateInfo(null);
        setIsNumberSafe(false);
        checkDuplicateLead(callData.mobileNumber, selectedFormId);
    }, [callData, signature, selectedFormId]);

    useEffect(() => {
        if (open && selectedCompany.companyId) {
            loadForms();
            loadAddressData();
        }
        if (!open) resetAll();
    }, [open]);

    useEffect(() => {
        if (!selectedFormData || !callData) return;
        const preFilled: Record<string, string> = {};
        selectedFormData.structure?.forEach((field) => {
            if (isMobileField(field.label)) preFilled[field.label] = callData.mobileNumber;
            else if (isNameField(field.label)) preFilled[field.label] = callData.name;
        });
        setResponses((prev) => ({ ...prev, ...preFilled }));
    }, [selectedFormData, callData]);

    const loadForms = async () => {
        try {
            setIsLoadingForms(true);
            const res = await api.post("leadService/getLeadFormsByCompany", {
                signature,
                company_id: selectedCompany.companyId,
                company_type: selectedCompany.companyType,
            });
            const fetched: LeadForm[] = res.data.data.data || [];
            setForms(fetched);
            if (fetched.length > 0) selectForm(fetched[0]);
        } catch (e) {
            notify.error("Failed to load forms!");
        } finally {
            setIsLoadingForms(false);
        }
    };

    const loadAddressData = async () => {
        try {
            const [cRes, sRes, dRes, ciRes] = await Promise.all([
                api.post("userService/getCountry", { signature }),
                api.post("userService/getState", { signature }),
                api.post("userService/getDistrict", { signature }),
                api.post("userService/getCity", { signature }),
            ]);
            setCountries(cRes.data.data);
            setStatesAll(sRes.data.data);
            setDistrictAll(dRes.data.data);
            setCitiesAll(ciRes.data.data);

            const india = cRes.data.data.find((c: any) => c.name.toLowerCase() === "india");
            if (india) {
                setSelectedCountry(`${india.name}-${india.id}`);
                setResponses((prev) => ({ ...prev, Country: india.name }));
                setStates(sRes.data.data.filter((s: any) => parseInt(s.country_id) === india.id));
            }
        } catch (e) {
            notify.error("Failed to load address data!");
        }
    };

    const selectForm = async (form: LeadForm) => {
        const newFormId = String(form.key);
        setSelectedFormId(newFormId);
        setSelectedFormData(form);
        setSelectedPipeline("");
        setFieldErrors({});
        setPipelines([]);
        // Reset duplicate state — will be re-evaluated by the effect watching selectedFormId
        setEarlyDuplicate(false);
        setDuplicateInfo(null);
        setIsNumberSafe(false);
        setIsDuplicate(false);

        if (form.pipeline_id) {
            try {
                const res = await api.post("leadService/getAllPipelinesById", { signature, id: form.pipeline_id });
                const stages: string[] = res.data.data.data?.[0]?.Stages || [];
                setPipelines(stages);
            } catch (e) {
                notify.error("Failed to load pipelines!");
            }
        }
    };

    const updateResponse = (label: string, value: string) => {
        setResponses((prev) => ({ ...prev, [label]: value }));
        setFieldErrors((prev) => { const n = { ...prev }; delete n[label]; return n; });
        if (submitError) setSubmitError(null);
        if (isDuplicate) setIsDuplicate(false);
    };

    const onCountryChange = (val: string) => {
        setSelectedCountry(val);
        setSelectedState("");
        const [name, id] = val.split("-");
        updateResponse("Country", name);
        setStates(statesAll.filter((s) => parseInt(s.country_id) === parseInt(id)));
        setDistricts([]);
        setCities([]);
        setResponses((prev) => ({ ...prev, State: "", District: "", City: "" }));
    };

    const onStateChange = (val: string) => {
        setSelectedState(val);
        const [name, id] = val.split("-");
        updateResponse("State", name);
        setDistricts(districtAll.filter((d) => parseInt(d.state_id) === parseInt(id)));
        setCities(citiesAll.filter((c) => parseInt(c.state_id) === parseInt(id)));
        setResponses((prev) => ({ ...prev, District: "", City: "" }));
    };

    const onDistrictChange = (val: string) => updateResponse("District", val.split("-")[0]);
    const onCityChange = (val: string) => updateResponse("City", val.split("-")[0]);

    const validate = (): boolean => {
        if (!selectedFormData) return false;
        const errors: Record<string, string> = {};
        selectedFormData.structure?.forEach((field) => {
            if (field.required && field?.type?.toLowerCase() !== "country") {
                const val = responses[field.label];
                if (!val?.trim()) errors[field.label] = `${field.label} is required`;
            }
        });
        setFieldErrors(errors);

        if (Object.keys(errors).length > 0) {
            const firstError = Object.values(errors)[0];
            notify.error(firstError);
            setTimeout(() => {
                if (!selectedFormId && formRef.current) {
                    formRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
                } else if (Object.keys(errors).length > 0 && fieldsRef.current) {
                    fieldsRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
                }
            }, 50);
            return false;
        }

        return true;
    };

    const handleSubmit = async () => {
        if (!validate() || !selectedFormData) return;
        try {
            setIsSubmitting(true);
            setSubmitError(null);
            setIsDuplicate(false);

            const payload: any = {
                form_id: selectedFormData.key,
                response: responses,
                company_id: selectedCompany.companyId,
                company_type: selectedCompany.companyType,
                signature,
                lead_source: "Direct Calling",
                lead_source_child: "Outbound Call (Manual)",
                priority: "High",
                assigned_lead_type: "Automatic",
                assigned_to: null,
                ...(selectedPipeline && { pipeline: selectedPipeline }),
            };

            await api.post("leadService/addLeadRespone", payload);
            setSuccessMessage("Lead added to Zyvo successfully!");
            notify.success("Lead added to Zyvo successfully!");
            setTimeout(onSuccess, 1200);
        } catch (err: any) {
            const status = err?.response?.status;
            const msg =
                err?.response?.data?.message?.[0]?.msg ||
                err?.response?.data?.message ||
                err?.data?.message?.[0]?.msg ||
                err?.message ||
                "Something went wrong. Please try again.";

            if (status === 409 || /already|duplicate|exists/i.test(msg)) setIsDuplicate(true);
            setSubmitError(msg);
            notify.error(msg);
        } finally {
            setIsSubmitting(false);
        }
    };

    const resetAll = () => {
        setForms([]);
        setSelectedFormId("");
        setSelectedFormData(null);
        setResponses({});
        setSelectedPipeline("");
        setPipelines([]);
        setSubmitError(null);
        setFieldErrors({});
        setSuccessMessage(null);
        setStates([]);
        setDistricts([]);
        setCities([]);
        setSelectedCountry("");
        setSelectedState("");
        setIsSubmitting(false);
        setIsDuplicate(false);
        setEarlyDuplicate(false);
        setIsCheckingDuplicate(false);
        setDuplicateInfo(null);
        setIsNumberSafe(false);
    };

    const renderField = (field: FormField) => {
        const { label, type, options, required } = field;
        const val = responses[label] ?? "";
        const err = fieldErrors[label];
        const mobile = isMobileField(label);

        const LabelNode = (
            <span style={{ fontSize: 12, fontWeight: 600, color: "#374151", letterSpacing: 0.3 }}>
                {label.toUpperCase()}
                {required && <span style={{ color: "#ef4444" }}> *</span>}
            </span>
        );

        const ErrNode = err ? (
            <div style={{ fontSize: 12, color: "#ef4444", marginTop: 4, padding: "4px 10px", background: "#fff5f5", border: "1px solid #fecaca", borderRadius: 6 }}>
                {err}
            </div>
        ) : null;

        if (type?.toLowerCase() === "country") {
            const cols = [
                { sub: "Country", list: countries, value: selectedCountry, getOpt: (x: any) => ({ val: `${x.name}-${x.id}`, lbl: x.name }), onChange: onCountryChange, disabled: false },
                { sub: "State", list: states, value: selectedState, getOpt: (x: any) => ({ val: `${x.name}-${x.id}`, lbl: x.name }), onChange: onStateChange, disabled: states.length === 0 },
                { sub: "District", list: districts, getOpt: (x: any) => ({ val: `${x.district}-${x.id}`, lbl: x.district }), onChange: onDistrictChange, disabled: districts.length === 0 },
                { sub: "City", list: cities, getOpt: (x: any) => ({ val: `${x.name}-${x.id}`, lbl: x.name }), onChange: onCityChange, disabled: cities.length === 0 },
            ];
            return (
                <Form.Item key={label} label={LabelNode} style={{ marginBottom: 16 }}>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
                        {cols.map(({ sub, list, value, getOpt, onChange, disabled }) => (
                            <div key={sub}>
                                <Text style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 4 }}>{sub}</Text>
                                <select className="form-control" value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} style={{ height: 32, fontSize: 13, borderRadius: 6, padding: "0 8px" }}>
                                    <option value="">Select</option>
                                    {list.map((item: any) => {
                                        const { val, lbl } = getOpt(item);
                                        return <option key={item.id} value={val}>{lbl}</option>;
                                    })}
                                </select>
                            </div>
                        ))}
                    </div>
                </Form.Item>
            );
        }

        if (type === "date") {
            return (
                <Form.Item key={label} label={LabelNode} style={{ marginBottom: 16 }}>
                    <DatePicker
                        style={{ width: "100%" }}
                        onChange={(date: any, dateString: string | string[]) => {
                            updateResponse(label, Array.isArray(dateString) ? dateString[0] : dateString);
                        }}
                        status={err ? "error" : undefined}
                        placeholder="Select date"
                    />
                    {ErrNode}
                </Form.Item>
            );
        }

        if (type === "select" && Array.isArray(options)) {
            return (
                <Form.Item key={label} label={LabelNode} style={{ marginBottom: 16 }}>
                    <Select value={val || undefined} placeholder={`Select ${label}`} style={{ width: "100%" }} status={err ? "error" : undefined} onChange={(v) => updateResponse(label, v)}>
                        {options.map((opt: string) => <Option key={opt} value={opt}>{opt}</Option>)}
                    </Select>
                    {ErrNode}
                </Form.Item>
            );
        }

        if (type === "textarea") {
            return (
                <Form.Item key={label} label={LabelNode} style={{ marginBottom: 16 }}>
                    <Input.TextArea rows={3} value={val} placeholder={`Enter ${label}`} status={err ? "error" : undefined} style={{ borderRadius: 0, fontSize: 13, resize: "none", padding: "10px 13px" }} onChange={(e) => updateResponse(label, e.target.value)} />
                    {ErrNode}
                </Form.Item>
            );
        }

        return (
            <Form.Item key={label} label={LabelNode} style={{ marginBottom: 16 }}>
                <Input
                    value={val}
                    placeholder={mobile ? "" : `Enter ${label}`}
                    readOnly={mobile}
                    status={err ? "error" : undefined}
                    style={{ borderRadius: 8, fontSize: 13, background: mobile ? "#f9fafb" : undefined, color: mobile ? "#374151" : undefined, cursor: mobile ? "default" : undefined, padding: "10px 13px" }}
                    onChange={(e) => { if (!mobile) updateResponse(label, e.target.value); }}
                    suffix={mobile ? <Tag color="blue" style={{ fontSize: 10, margin: 0 }}>Auto-filled</Tag> : undefined}
                />
                {ErrNode}
            </Form.Item>
        );
    };

    return (
        <AntdModal
            open={open}
            closable={false}
            maskClosable={false}
            keyboard={false}
            centered
            width={600}
            zIndex={2000}
            getContainer={() => document.body}
            styles={MODAL_STYLES}
            title={
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 36, height: 36, background: "#eff6ff", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17, flexShrink: 0 }}>
                        <NotebookPen />
                    </div>
                    <Space>
                        <span style={{ fontSize: 15, fontWeight: 600, color: "#0f172a" }}>Add Lead to Zyvo</span>
                        {callData && (
                            <Tag color="green" style={{ fontSize: 12 }}>
                                {callData.name} · {callData.mobileNumber}
                            </Tag>
                        )}
                    </Space>
                </div>
            }
            footer={
                <div style={{ display: "flex", justifyContent: "center", gap: 10 }}>
                    <Button
                        size="middle"
                        style={{ minWidth: 148, borderRadius: 12, fontWeight: 600, fontSize: 13, height: 40, borderColor: "#e2e8f0", color: "#475569", background: "#f8fafc" }}
                        disabled={isSubmitting || !!successMessage}
                        onClick={onSkip}
                    >
                        Skip & Next Call
                    </Button>
                    <Button
                        type="primary"
                        size="middle"
                        style={{ minWidth: 148, borderRadius: 12, fontWeight: 600, fontSize: 13, height: 40, background: "#1D71E3", border: "none" }}
                        loading={isSubmitting}
                        disabled={!!successMessage || isLoadingForms || isCheckingDuplicate || earlyDuplicate}
                        onClick={handleSubmit}
                    >
                        Save & Next Call
                    </Button>
                </div>
            }
        >
            {isCheckingDuplicate && (
                <div style={{ marginBottom: 16, padding: "10px 14px", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8, display: "flex", alignItems: "center", gap: 8 }}>
                    <Spin size="small" />
                    <Text style={{ fontSize: 13, color: "#1d4ed8" }}>Checking if this number already exists…</Text>
                </div>
            )}

            {!isCheckingDuplicate && isNumberSafe && !earlyDuplicate && (
                <div style={{ marginBottom: 16, padding: "10px 14px", background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 8, display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 16 }}>✅</span>
                    <Text style={{ fontSize: 13, color: "#15803d", fontWeight: 500 }}>
                        New number — you can add this lead.
                    </Text>
                </div>
            )}

            {!isCheckingDuplicate && earlyDuplicate && (
                <div style={{ marginBottom: 16, padding: "12px 14px", background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 8 }}>
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontSize: 18 }}>⚠️</span>
                            <div>
                                <Text style={{ fontSize: 13, fontWeight: 600, color: "#92400e", display: "block" }}>Duplicate Lead Detected</Text>
                                <Text style={{ fontSize: 12, color: "#b45309" }}>{duplicateInfo || "This mobile number already exists in Zyvo."}</Text>
                            </div>
                        </div>
                        <Button size="small" onClick={onSkip} style={{ background: "#f59e0b", border: "none", color: "#fff", borderRadius: 6, fontWeight: 600, flexShrink: 0 }}>
                            Skip & Next
                        </Button>
                    </div>
                </div>
            )}

            {successMessage && (
                <div style={{ marginBottom: 16, padding: "10px 14px", background: "#d1fae5", border: "1px solid #6ee7b7", borderRadius: 8, display: "flex", alignItems: "center", gap: 8 }}>
                    <span>✅</span>
                    <Text style={{ fontSize: 13, color: "#065f46", fontWeight: 500 }}>{successMessage}</Text>
                </div>
            )}

            {isDuplicate && (
                <div style={{ marginBottom: 16, padding: "10px 14px", background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span>⚠️</span>
                        <Text style={{ fontSize: 13, color: "#92400e" }}>{submitError || "This mobile number already exists in Zyvo."}</Text>
                    </div>
                    <Button size="small" onClick={onSkip} style={{ background: "#f59e0b", border: "none", color: "#fff", borderRadius: 6, fontWeight: 600 }}>
                        Skip & Next
                    </Button>
                </div>
            )}

            {submitError && !isDuplicate && (
                <div style={{ marginBottom: 16, padding: "8px 12px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, fontSize: 13, color: "#b91c1c" }}>
                    {submitError}
                </div>
            )}

            {isLoadingForms ? (
                <div style={{ display: "flex", justifyContent: "center", padding: "60px 0" }}>
                    <Spin size="large" tip="Loading form..." />
                </div>
            ) : (
                <Form layout="vertical">
                    <div ref={formRef}>
                        {forms.length > 0 && (
                            <Form.Item style={{ marginBottom: 18 }}>
                                <div style={{ fontSize: 12, fontWeight: 600, color: "#64748b", letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 8 }}>
                                    Select Lead Form <span style={{ color: "#ef4444" }}>*</span>
                                </div>
                                <div style={{ border: "1.5px solid #e2e8f0", borderRadius: 12, overflow: "hidden", transition: "border-color 0.2s" }}>
                                    <Select
                                        bordered={false}
                                        value={selectedFormId || undefined}
                                        placeholder="Select a form"
                                        style={{ width: "100%", height: 40 }}
                                        dropdownStyle={{ borderRadius: 12 }}
                                        onChange={(val: string) => {
                                            const form = forms.find((f) => String(f.key) === String(val));
                                            if (form) selectForm(form);
                                        }}
                                        optionLabelProp="label"
                                    >
                                        {forms.map((f) => (
                                            <Option key={String(f.key)} value={String(f.key)} label={f.LeadFormName}>
                                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                    <span style={{ fontSize: 13 }}>{f.LeadFormName}</span>
                                                    {String(f.key) === selectedFormId && (
                                                        <Tag style={{ fontSize: 10, margin: 0, borderRadius: 20, background: "#dbeafe", color: "#2563eb", border: "none" }}>Active</Tag>
                                                    )}
                                                </div>
                                            </Option>
                                        ))}
                                    </Select>
                                </div>
                            </Form.Item>
                        )}
                    </div>

                    <div ref={pipelineRef}>
                        {pipelines.length > 0 && (
                            <Form.Item style={{ marginBottom: 18 }}>
                                <div style={{ fontSize: 12, fontWeight: 600, color: "#64748b", letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 8 }}>
                                    Pipeline Stage{" "}
                                    <span style={{ fontWeight: 500, color: "#9ca3af", textTransform: "none" }}>(Optional)</span>
                                </div>
                                <div style={{ border: "1.5px solid #e2e8f0", borderRadius: 12, overflow: "hidden", transition: "border-color 0.2s" }}>
                                    <Select bordered={false} value={selectedPipeline || undefined} placeholder="Select a pipeline stage" style={{ width: "100%", height: 40 }} allowClear onChange={(val) => setSelectedPipeline(val || "")}>
                                        {pipelines.map((p) => <Option key={p} value={p}>{p}</Option>)}
                                    </Select>
                                </div>
                            </Form.Item>
                        )}
                    </div>

                    <div ref={fieldsRef}>
                        {selectedFormData && (
                            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: "#9ca3af", marginBottom: 16, borderBottom: "1px solid #f1f5f9", paddingBottom: 8 }}>
                                Lead Information
                            </div>
                        )}

                        {selectedFormData?.structure?.map(renderField)}

                        {!selectedFormData && !isLoadingForms && (
                            <div style={{ textAlign: "center", padding: "40px 0", color: "#9ca3af", background: "#f9fafb", borderRadius: 12, border: "1px dashed #e5e7eb" }}>
                                No lead form available for this company.
                            </div>
                        )}
                    </div>
                </Form>
            )}
        </AntdModal>
    );
};

export default AddLeadToZyvo;
